import React, { FC } from 'react';
import { Dialog, DialogContent, DialogActions, DialogTitle, Button, IconButton, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useScheduleDesviationForm } from '../../../hooks/scheduleDesviation/useScheduleDesviationForm';
import CloseIcon from '@mui/icons-material/Close';
import { PaperComponent } from '../../../shared/ui/draggable/Draggable';
import { AlertCustom } from '../../../shared/ui/alert/AlertCustom';
import { ScheduleDesviationRule } from 'repository/timekeeper/@types/ScheduleDesviationRuleModel';
import { ScheduleDesviationForm } from './ScheduleDesviationForm';
import { FormDelete } from 'shared/ui/form/FormDelete';

type Props = {
    open: boolean;
    onClose: () => void;
    rows: ScheduleDesviationRule[];
    selectedRow: ScheduleDesviationRule[];
    reloading: () => void;
    mode: 'add' | 'edit' | 'delete' | 'info' | 'duplicate';
};

export const ScheduleDesviationDialog: FC<Props> = ({ open, onClose, reloading, rows, selectedRow, mode }) => {
    const { t } = useTranslation();
    const form = useScheduleDesviationForm({ rows, onClose, reloading, mode, selectedRow, open });

    return (
        <Dialog
            maxWidth="xs"
            fullWidth
            open={open}
            PaperComponent={PaperComponent}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                if (mode === 'delete') {
                    onClose();
                } else {
                    form.detectChanges();
                }
            }}
            aria-labelledby="draggable-dialog-title"
            aria-describedby="draggable-alert-dialog-description"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {mode === 'add' && t('settings.schedule_desviation.table.dialog.add.title')}
                {mode === 'edit' && t('settings.schedule_desviation.table.dialog.edit.title')}
                {mode === 'duplicate' && ` ${t('settings.schedule_desviation.table.dialog.duplicate.title')}`}
                {mode === 'info' && t('settings.schedule_desviation.table.dialog.info.title')}
                {mode === 'delete' && t('settings.schedule_desviation.table.dialog.delete.title')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => {
                    if (mode === 'delete') {
                        onClose();
                    } else {
                        form.detectChanges();
                    }
                }}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>{form.alert && <AlertCustom alerts={form.alert} setAlerts={form.setAlert} closeForm={form.cleanForm} t={t} />}</Box>
            <DialogContent dividers>
                {mode === 'delete' ? (
                    <FormDelete
                        open={open}
                        title={t('settings.schedule_desviation.table.dialog.delete.title')}
                        description={t('settings.schedule_desviation.table.dialog.delete.description')}
                        onCancel={form.cleanForm}
                        onConfirm={form.handleSubmit}
                        names={selectedRow.map((row) => row.name)}
                        textButton={t('general.buttons.delete')}
                        mode={mode}
                    />
                ) : (
                    <ScheduleDesviationForm form={form} mode={mode} />
                )}
            </DialogContent>
            {mode === 'info' && (
                <DialogActions>
                    <Button variant="contained" color="error" onClick={onClose}>
                        {t('general.buttons.close')}
                    </Button>
                </DialogActions>
            )}
            {mode !== 'delete' && mode !== 'info' && (
                <DialogActions>
                    <Button variant="contained" color="error" onClick={() => form.cleanForm()}>
                        {t('general.buttons.cancel')}
                    </Button>
                    <Button variant="contained" color="primary" disabled={form.disabled} onClick={form.handleSubmit}>
                        {mode === 'add' ? t('general.buttons.add') : t('general.buttons.save')}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};
